import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  CategoryAggregate,
  CustomerBonus,
  FastSumsTypes,
  InputTypes,
  IntervalsTypes,
  PaymentMethodBase,
  PaymentMethodCategory,
  PaymentMethodConfiguration,
  PaymentTypes,
  RedirectContainers,
  TaxConfiguration,
} from '@cashier/cashier-model';

export const inputIconsMap = {
  [InputTypes.Cvv]: 'assets/icons/lock-solid.svg',
  [InputTypes.ExpirationDate]: 'assets/icons/calendar.svg',
};

export const inputMasksMap = {
  [InputTypes.ExpirationDate]: '99/99',
};

export const DEFAULT_CATEGORY_NAME = 'recommended';

export interface PaymentMethodsState {
  arePaymentMethodsLoading: boolean;
  isPaymentMethodSelectedManually: boolean;
  loadCertainPaymentMethod: boolean;
  categoryMap: { [id: string]: CategoryAggregate };
  paymentMethod: PaymentMethodConfiguration;
  isCategoryFilterActivated: boolean;
  activeCategory: string | null;
  country: string | null;
  paymentIqMerchantId: string | null;
  registrationRequired: boolean;
  tax: {
    [key: string]: TaxConfiguration;
  };
  bonus: CustomerBonus;
  error: string | null;
}

const getDefaultPaymentMethod = (categoryMap: {
  [id: string]: CategoryAggregate;
}): PaymentMethodBase | null => {
  const categoryEntries = [...Object.entries(categoryMap)];
  if (!categoryEntries.length) {
    return null;
  }

  const defaultCategories = categoryEntries
    .filter(({ 1: { name: value } }) => value === DEFAULT_CATEGORY_NAME)
    .map(([k]) => k);

  const [defaultCategoryKey] = defaultCategories.length
    ? defaultCategories
    : Object.keys(categoryMap);

  return categoryMap[defaultCategoryKey].paymentMethods[0];
};

export const PAYMENT_METHODS_FEATURE_KEY = 'paymentMethods';

export const initialPaymentMethodsState: PaymentMethodsState = {
  arePaymentMethodsLoading: false,
  loadCertainPaymentMethod: false,
  isPaymentMethodSelectedManually: false /* for the needs of mobile version */,
  paymentMethod: {
    id: '',
    name: '',
    vendor: '',
    inputs: [],
    activeInputs: [],
    account_form_inputs: [],
    categories: [],
    logo: '',
    ruleParams: {
      min: null,
      max: null,
      recommended: false,
      amounts: [],
    },
    next_step_info: {
      channel: null,
      post_confirm: null,
      post_process: null,
    },
    description: '',
    source: null,
    referenceId: '',
    hideAmount: false,
    redirectContainer: RedirectContainers.NO_REDIRECT,
    preprocessor: null,
    restriction: null,
  },
  bonus: {
    amountIntervals: [],
    cashierConfiguration: {
      displayBonusFastSums: false,
      displayBonusCalculator: false,
      iconURL: '',
    },
    id: '',
    fastSumsType: FastSumsTypes.Amount,
    intervalsType: IntervalsTypes.Reward,
  },
  categoryMap: {},
  isCategoryFilterActivated: false,
  activeCategory: null,
  country: null,
  paymentIqMerchantId: null,
  registrationRequired: false,
  tax: {},
  error: null,
};

export const paymentMethodsSlice = createSlice({
  name: PAYMENT_METHODS_FEATURE_KEY,
  initialState: initialPaymentMethodsState,
  reducers: {
    fetchPaymentMethodsStart: (state, action: PayloadAction<PaymentTypes>) => {
      state.arePaymentMethodsLoading = true;
    },
    fetchPaymentMethodsSuccess: (
      state,
      action: PayloadAction<{
        categories: PaymentMethodCategory[];
        payment_methods: PaymentMethodBase[];
      }>,
    ) => {
      state.arePaymentMethodsLoading = false;
      const { categories, payment_methods } = action.payload;
      const categoryMap: { [id: string]: CategoryAggregate } = {};

      categories.forEach((category) => {
        const { id, name, label } = category;
        const filteredPaymentMethods = payment_methods.filter(
          ({ categories }) => categories.includes(id),
        );

        if (filteredPaymentMethods.length) {
          categoryMap[id] = {
            name,
            label,
            paymentMethods: filteredPaymentMethods,
          };
        }
      });

      state.categoryMap = categoryMap;

      const defaultPaymentMethod = getDefaultPaymentMethod(state.categoryMap);
      if (defaultPaymentMethod) {
        const { categories, ...paymentMethod } = defaultPaymentMethod;

        state.paymentMethod = {
          ...initialPaymentMethodsState.paymentMethod,
          ...paymentMethod,
        };
        state.loadCertainPaymentMethod = true;
      } else {
        state.paymentMethod = initialPaymentMethodsState.paymentMethod;
      }
    },
    fetchPaymentMethodsFailure: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.arePaymentMethodsLoading = false;
    },
    fetchCertainPaymentMethodStart: (
      state,
      {
        payload,
      }: PayloadAction<{
        paymentType: PaymentTypes;
        paymentMethodId: string;
        bonusCode?: string;
      }>,
    ) => {
      state.arePaymentMethodsLoading = true;
      state.paymentMethod = { ...state.paymentMethod, inputs: [] };
    },
    fetchCertainPaymentMethodSuccess: (
      state,
      { payload }: PayloadAction<any>,
    ) => {
      const { method } = payload;
      state.paymentMethod = {
        ...state.paymentMethod,
        ...method,
        hideAmount: !payload.method.inputs.find(
          // ({ name }: { name: string }) => name === AMOUNT_FIELD_NAME,
          ({ name }: { name: string }) => name === 'amount',
        ),
      };
      state.bonus = payload.attributes.bonus;
      state.paymentIqMerchantId = payload.attributes?.payment_iq_merchant_id;
      state.tax = payload.attributes?.tax || {};
      state.country = payload.country;
      state.registrationRequired = payload.registrationRequired;
      state.arePaymentMethodsLoading = false;
      state.loadCertainPaymentMethod = false;
    },
    fetchCertainPaymentMethodFailure: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.error = action.payload;
      state.arePaymentMethodsLoading = false;
      state.loadCertainPaymentMethod = false;
    },
    choosePaymentMethod: (state, action: PayloadAction<PaymentMethodBase>) => {
      if (state.paymentMethod.id !== action.payload.id) {
        const { categories, ...paymentMethod } = action.payload;
        state.paymentMethod = {
          ...initialPaymentMethodsState.paymentMethod,
          ...paymentMethod,
        };
        state.loadCertainPaymentMethod = true;
      }

      state.isPaymentMethodSelectedManually = true;
    },
    resetPaymentMethod: (state) => {
      const defaultPaymentMethod = getDefaultPaymentMethod(state.categoryMap);

      if (defaultPaymentMethod) {
        if (defaultPaymentMethod?.id !== state.paymentMethod.id) {
          const { categories, ...paymentMethod } = defaultPaymentMethod;
          state.paymentMethod = {
            ...initialPaymentMethodsState.paymentMethod,
            ...paymentMethod,
          };
        }

        state.loadCertainPaymentMethod = true;
      }

      state.isPaymentMethodSelectedManually = false;
    },
    toggleCategoryFilterActivation: (state) => {
      state.isCategoryFilterActivated = !state.isCategoryFilterActivated;
    },
    setActiveCategory: (state, action: PayloadAction<string>) => {
      state.activeCategory = action.payload;

      if (Object.keys(state.categoryMap).length) {
        const [firstCategoryId] = Object.keys(state.categoryMap);

        const {
          paymentMethods: [firstMethod],
        } =
          state.categoryMap[action.payload] ||
          state.categoryMap[firstCategoryId];
        const { categories, ...paymentMethod } = firstMethod;

        if (state.paymentMethod.id !== paymentMethod.id) {
          state.paymentMethod = {
            ...state.paymentMethod,
            ...paymentMethod,
          };
          state.loadCertainPaymentMethod = true;
        }
      }

      state.isCategoryFilterActivated = false;
    },
  },
});

export const paymentMethodsReducer = paymentMethodsSlice.reducer;

export const {
  fetchPaymentMethodsStart,
  fetchPaymentMethodsSuccess,
  fetchPaymentMethodsFailure,
  fetchCertainPaymentMethodStart,
  fetchCertainPaymentMethodSuccess,
  fetchCertainPaymentMethodFailure,
  choosePaymentMethod,
  resetPaymentMethod,
  toggleCategoryFilterActivation,
  setActiveCategory,
} = paymentMethodsSlice.actions;
